// Header.js
import React, { useState, useRef } from "react";
import { Link } from "react-router-dom";
import { Menu, X, ChevronDown } from "lucide-react";
import logo from "../assets/black_logo.png";

const Header = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isAboutOpen, setIsAboutOpen] = useState(false);
  const [isServicesOpen, setIsServicesOpen] = useState(false);

  const aboutTimeoutRef = useRef(null);
  const servicesTimeoutRef = useRef(null);

  const aboutLinks = [
    { name: "Frequently Asked Questions", slug: "faq" },
    { name: "Meet the Team", slug: "meet-the-team" },
    { name: "Reviews & Testimonials", slug: "reviews-testimonials" },
  ];

  const homeCareServices = [
    { name: "Alzheimer's and Dementia Care", slug: "alzheimers-dementia-care" },
    { name: "Companionship Care", slug: "companionship-care" },
    { name: "Hospice Care", slug: "hospice-care" },
    { name: "Personal Care", slug: "personal-care" },
    { name: "Post-Operative Care", slug: "post-operative-care" },
    { name: "Respite Care", slug: "respite-care" },
    { name: "Specialty Care", slug: "specialty-care" },
    { name: "Spinal Cord Injury Care", slug: "spinal-cord-injury-care" },
  ];

  const handleMouseEnter = (setter, timeoutRef) => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    setter(true);
  };

  const handleMouseLeave = (setter, timeoutRef) => {
    timeoutRef.current = setTimeout(() => {
      setter(false);
    }, 150);
  };

  return (
    <header className="w-full px-6 md:px-16 lg:px-24 py-4 bg-white shadow-sm sticky top-0 z-50">
      <div className="flex items-center justify-between">
        {/* Logo */}
        <Link to="/" className="flex items-center">
          <img src={logo} alt="Allawah Lodge" className="h-10 md:h-12" />
        </Link>

        {/* Desktop Navigation */}
        <nav className="hidden lg:flex items-center gap-8 text-sm font-medium text-gray-700">
          {/* About Dropdown */}
          <div
            className="relative"
            onMouseEnter={() =>
              handleMouseEnter(setIsAboutOpen, aboutTimeoutRef)
            }
            onMouseLeave={() =>
              handleMouseLeave(setIsAboutOpen, aboutTimeoutRef)
            }
          >
            <button className="flex items-center gap-1 hover:text-[#E85D2C] transition-colors">
              ABOUT
              <ChevronDown size={16} />
            </button>

            {isAboutOpen && (
              <div className="absolute top-full left-0 pt-2 z-50">
                <div className="w-56 bg-white border border-gray-200 rounded-lg shadow-lg py-2">
                  {aboutLinks.map((link) => (
                    <Link
                      key={link.slug}
                      to={`/about/${link.slug}`}
                      className="block px-4 py-2.5 hover:bg-gray-50 text-gray-700 hover:text-[#E85D2C] transition-colors"
                    >
                      {link.name}
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Home Care Services Dropdown */}
          <div
            className="relative"
            onMouseEnter={() =>
              handleMouseEnter(setIsServicesOpen, servicesTimeoutRef)
            }
            onMouseLeave={() =>
              handleMouseLeave(setIsServicesOpen, servicesTimeoutRef)
            }
          >
            <button className="flex items-center gap-1 hover:text-[#E85D2C] transition-colors">
              HOME CARE SERVICES
              <ChevronDown size={16} />
            </button>

            {isServicesOpen && (
              <div className="absolute top-full left-0 pt-2 z-50">
                <div className="w-56 bg-white border border-gray-200 rounded-lg shadow-lg py-2">
                  {homeCareServices.map((service) => (
                    <Link
                      key={service.slug}
                      to={`/services/${service.slug}`}
                      className="block px-4 py-2.5 hover:bg-gray-50 text-gray-700 hover:text-[#E85D2C] transition-colors"
                    >
                      {service.name}
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </div>

          <Link
            to="/careers"
            className="hover:text-[#E85D2C] transition-colors"
          >
            CAREERS
          </Link>
        </nav>

        {/* Contact Button */}
        <Link
          to="/contact"
          className="hidden lg:block bg-[#E85D2C] hover:bg-[#d54d1f] text-white font-semibold px-6 py-2.5 rounded-full transition-colors"
        >
          CONTACT US
        </Link>

        {/* Mobile Menu Button */}
        <button
          className="lg:hidden text-gray-700"
          onClick={() => setIsOpen(!isOpen)}
        >
          {isOpen ? <X size={28} /> : <Menu size={28} />}
        </button>
      </div>

      {/* Mobile Navigation */}
      {isOpen && (
        <div className="lg:hidden absolute top-full left-0 w-full bg-white shadow-lg py-4 px-6 space-y-4">
          {/* Mobile About */}
          <div>
            <button
              className="w-full text-left font-medium text-gray-700 py-2 flex items-center justify-between"
              onClick={() => setIsAboutOpen(!isAboutOpen)}
            >
              ABOUT
              <ChevronDown
                size={16}
                className={`transition-transform ${isAboutOpen ? "rotate-180" : ""}`}
              />
            </button>
            {isAboutOpen && (
              <div className="pl-4 space-y-2 mt-2">
                {aboutLinks.map((link) => (
                  <Link
                    key={link.slug}
                    to={`/about/${link.slug}`}
                    className="block py-2 text-sm text-gray-600 hover:text-[#E85D2C]"
                    onClick={() => setIsOpen(false)}
                  >
                    {link.name}
                  </Link>
                ))}
              </div>
            )}
          </div>

          {/* Mobile Services */}
          <div>
            <button
              className="w-full text-left font-medium text-gray-700 py-2 flex items-center justify-between"
              onClick={() => setIsServicesOpen(!isServicesOpen)}
            >
              HOME CARE SERVICES
              <ChevronDown
                size={16}
                className={`transition-transform ${isServicesOpen ? "rotate-180" : ""}`}
              />
            </button>
            {isServicesOpen && (
              <div className="pl-4 space-y-2 mt-2">
                {homeCareServices.map((service) => (
                  <Link
                    key={service.slug}
                    to={`/services/${service.slug}`}
                    className="block py-2 text-sm text-gray-600 hover:text-[#E85D2C]"
                    onClick={() => setIsOpen(false)}
                  >
                    {service.name}
                  </Link>
                ))}
              </div>
            )}
          </div>

          <Link
            to="/careers"
            className="block font-medium text-gray-700 py-2 hover:text-[#E85D2C]"
            onClick={() => setIsOpen(false)}
          >
            CAREERS
          </Link>

          <Link
            to="/contact"
            className="block w-full text-center bg-[#E85D2C] hover:bg-[#d54d1f] text-white font-semibold px-6 py-2.5 rounded-full transition-colors mt-4"
            onClick={() => setIsOpen(false)}
          >
            CONTACT US
          </Link>
        </div>
      )}
    </header>
  );
};

export default Header;
